/**
 * One-time recovery-code reveal (portal-spec §4.1 steps 5–7). Shows the 24 words,
 * offers print/download (client-rendered — never transmitted), then gates
 * dismissal behind a type-back of a few positions so the user proves they saved
 * it before the code is wiped from the UI.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../store/session';
import { Button, ErrorNote, Field, Input } from './ui';

export function RecoveryCodeModal({ code }: { code: string }) {
  const acknowledge = useSession((s) => s.acknowledgeRecovery);
  const words = useMemo(() => code.split(/\s+/u).filter((w) => w.length > 0), [code]);
  const challenge = useMemo(() => {
    const raw = [
      Math.floor(words.length * 0.2),
      Math.floor(words.length * 0.5),
      Math.floor(words.length * 0.8),
    ];
    return [...new Set(raw)];
  }, [words.length]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog, trap Tab inside it, and restore focus on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = (): HTMLElement[] =>
      dialog === null
        ? []
        : Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'button, input, [href], [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.hasAttribute('disabled'));
    focusable()[0]?.focus();
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') {
        return;
      }
      const f = focusable();
      if (f.length === 0) {
        return;
      }
      const first = f[0] as HTMLElement;
      const last = f[f.length - 1] as HTMLElement;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener('keydown', onKeyDown);
    return () => {
      dialog?.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const verify = async (): Promise<void> => {
    const ok = challenge.every((i) => (answers[i] ?? '').trim().toLowerCase() === words[i]);
    if (!ok) {
      setErr('Those words do not match your saved copy. Try again.');
      return;
    }
    await acknowledge();
  };

  const download = (): void => {
    const blob = new Blob([`Heivol Locker — recovery code\n\n${code}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'heivol-locker-recovery-code.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recovery-code-title"
        className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-auto rounded-lg border border-border bg-surface p-6 shadow-card"
      >
        <h2 id="recovery-code-title" className="text-lg font-semibold text-text">
          Save your recovery code
        </h2>
        <p className="text-sm text-text-secondary">
          These 24 words are the <strong>only</strong> way back into your vault if you forget your
          passphrase. We can never see or reset them — if you lose both, your vault is gone forever.
          You will not see this code again.
        </p>
        <ol className="grid grid-cols-3 gap-x-4 gap-y-1 rounded-md border border-border bg-surface-alt p-3 font-mono text-sm text-text">
          {words.map((w, i) => (
            <li key={`${i}-${w}`}>
              <span className="mr-1 text-text-secondary tabular-nums">{i + 1}.</span>
              {w}
            </li>
          ))}
        </ol>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={download}>
            Download .txt
          </Button>
          <Button variant="ghost" onClick={() => window.print()}>
            Print
          </Button>
        </div>
        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm font-medium text-text">Confirm you saved it</p>
          {challenge.map((i) => (
            <Field key={i} label={`Word #${i + 1}`}>
              <Input
                value={answers[i] ?? ''}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
              />
            </Field>
          ))}
          {err !== null && <ErrorNote>{err}</ErrorNote>}
          <Button onClick={() => void verify()} className="w-full">
            I have saved my recovery code
          </Button>
        </div>
      </div>
    </div>
  );
}
